/* eslint-disable no-param-reassign */
import { DeviceSize, useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import addMonths from 'date-fns/addMonths';
import addYears from 'date-fns/addYears';
import differenceInCalendarMonths from 'date-fns/differenceInCalendarMonths';
import eachMonthOfInterval from 'date-fns/eachMonthOfInterval';
import startOfMonth from 'date-fns/startOfMonth';
import { debounce } from 'debounce';
import isEqual from 'lodash.isequal';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useOnClickOutside from 'use-onclickoutside';
import usePrevious from '../usePrevious';

export const GUTTER_SIZE = 32;

const getInterval = (): Interval => ({
    start: new Date(),
    end: addYears(new Date(), 2),
});

const isOverlapping = ([aStart, aEnd]: [number, number], [bStart, bEnd]: [number, number]) =>
    aStart <= bEnd && bStart <= aEnd;

const getActions = (
    viewportRef: MutableRefObject<HTMLDivElement | null>,
    deviceSize: DeviceSize | null,
    viewMode: ViewMode
) => {
    const getMonthElement = (index: number, viewportEl = viewportRef.current) =>
        viewportEl?.firstChild?.childNodes?.[index] as HTMLDivElement | undefined;

    const getMonthDimensions = (viewportEl = viewportRef.current) => {
        const monthElement = getMonthElement(0, viewportEl);

        return monthElement ? monthElement.clientWidth : 0;
    };

    const scrollTo = (options: { left?: number; top?: number }, condition = true, viewportEl = viewportRef.current) => {
        if (condition) {
            setTimeout(() =>
                viewportEl?.scrollTo({
                    ...options,
                    behavior: 'smooth',
                })
            );
        }
    };

    const scrollBy = (left: number, condition = true, viewportEl = viewportRef.current) => {
        if (condition) {
            viewportEl?.scrollBy({ left, behavior: 'smooth' });
        }
    };

    const scrollToToday = (condition = true) => {
        scrollTo({ left: 0 }, condition);
    };

    const getInViewIndexRange = (viewportEl = viewportRef.current): [number, number] => {
        const monthElement = getMonthElement(0);

        if (monthElement && viewportEl) {
            const onMobile = deviceSize === 'mobile';

            if (!onMobile || viewMode === 'horizontal') {
                const left = viewportEl.scrollLeft;
                const viewportWidth = viewportEl.clientWidth;
                // clientWidth and scrollLeft are rounded to whole pixels (Safari), so use the exact width and allow
                // for a pixel of rounding, otherwise the first month in view can be skipped
                const size = monthElement.getBoundingClientRect().width + GUTTER_SIZE;
                const firstMonthIndex = Math.ceil((left - 1) / size);
                const lastMonthIndex = Math.ceil((left + viewportWidth) / size - 1);
                return [Math.max(firstMonthIndex, 0), Math.max(lastMonthIndex, 0)];
            }

            if (onMobile) {
                const { clientHeight } = viewportEl;
                const vpRect = viewportEl.getBoundingClientRect();
                const vpStart = vpRect.top;
                const vpEnd = vpStart + clientHeight;
                // hacky AF
                const inViewIndexes = eachMonthOfInterval(getInterval()).reduce<number[]>((acc, _, i) => {
                    const rect = getMonthElement(i)?.getBoundingClientRect();
                    const monthStart = rect?.y ?? 0;
                    const monthEnd = rect ? rect.y + rect.height : 0;

                    // if these ranges are overlapping it means the current month is in view
                    if (isOverlapping([monthStart, monthEnd], [vpStart, vpEnd])) {
                        return [...acc, i];
                    }
                    return acc;
                }, []);

                if (inViewIndexes.length) {
                    return [Math.min(...inViewIndexes), Math.max(...inViewIndexes)];
                }
            }
        }
        return [0, 0];
    };

    const jumpToMonth = (date: Date | number, slow = false, viewportEl = viewportRef.current) => {
        const monthElement = getMonthElement(0, viewportEl);

        const onMobile = deviceSize === 'mobile';

        if (monthElement) {
            const diff = differenceInCalendarMonths(new Date(date), new Date());
            const scrollMonth = getMonthElement(diff, viewportEl);

            if (!viewportEl || !scrollMonth) {
                return;
            }
            if (!onMobile) {
                // We want to have the startDate as the middle month when there are three months in the viewport, otherwise
                // as the left month
                if (slow) {
                    scrollTo({ left: scrollMonth.offsetLeft });
                    return;
                }
                viewportEl.scrollLeft = scrollMonth?.offsetLeft;
            }

            if (onMobile) {
                if (slow) {
                    scrollTo({ top: scrollMonth.offsetTop });
                    return;
                }
                viewportEl.scrollTop = scrollMonth?.offsetTop;
            }
        }
    };

    return {
        getMonthElement,
        getMonthDimensions,
        scrollTo,
        scrollBy,
        scrollToToday,
        getInViewIndexRange,
        jumpToMonth,
    };
};

const getInViewInterval = (monthsInView: [number, number] | null) => {
    if (!monthsInView) {
        return null;
    }
    let [first, last] = monthsInView;
    if (first > last) {
        [first, last] = [last, first];
    }

    return {
        start: startOfMonth(addMonths(new Date(), first)),
        end: startOfMonth(addMonths(new Date(), last)),
    };
};

export type ViewMode = 'vertical' | 'horizontal';

export interface PlannerViewOptions {
    onClickOutside?: () => void;
    initialCenterMonth?: Date | number;
    onInViewIntervalChange?: (months: Date[]) => void;
    mode?: ViewMode;
}

const useOnMonthViewChange = (
    monthsInView: [number, number] | null,
    onInViewIntervalChange?: (months: Date[]) => void
) => {
    const prevMonthsInView = usePrevious(monthsInView);
    useEffect(() => {
        if (!isEqual(prevMonthsInView, monthsInView)) {
            const newInViewInterval = getInViewInterval(monthsInView);
            if (newInViewInterval) {
                onInViewIntervalChange?.(eachMonthOfInterval(newInViewInterval));
            }
        }
    }, [monthsInView, onInViewIntervalChange, prevMonthsInView]);
};

const usePlannerView = (options: PlannerViewOptions = {}) => {
    const { onClickOutside, onInViewIntervalChange } = options;
    // initial values shouldn't change so store it in a ref
    const initialCenterMonth = useRef(options.initialCenterMonth);

    const viewportRef = useRef<HTMLDivElement | null>(null);
    const months = eachMonthOfInterval(getInterval());
    const deviceSize = useDeviceSize();

    const viewActions = useMemo(
        () => getActions(viewportRef, deviceSize, options?.mode ?? 'vertical'),
        [deviceSize, options?.mode]
    );
    const [monthsInView, setMonthsInView] = useState<[number, number] | null>(null);
    useOnMonthViewChange(monthsInView, onInViewIntervalChange);

    const setNewMonthsInView = useCallback(() => {
        const inView = viewActions.getInViewIndexRange();

        if (!isEqual(monthsInView, inView)) {
            setMonthsInView(inView);
        }
    }, [viewActions, monthsInView]);

    useEffect(() => {
        if (deviceSize) {
            setNewMonthsInView();
        }
    }, [deviceSize, setNewMonthsInView]);

    useOnClickOutside(viewportRef as React.RefObject<HTMLElement>, () => {
        onClickOutside?.();
    });

    // TODO use useMemo in order te create the function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onScrollHandler = useCallback(debounce(setNewMonthsInView), [setNewMonthsInView]);
    const viewportRefCallback = useCallback(
        (node: HTMLDivElement) => {
            if (!node) {
                return;
            }
            if (initialCenterMonth.current) {
                viewActions.jumpToMonth(initialCenterMonth.current, undefined, node);
            }
            // eslint-disable-next-line no-param-reassign
            viewportRef.current = node;
        },
        [viewActions]
    );

    useEffect(() => {
        viewportRef.current?.addEventListener('scroll', onScrollHandler);
        return () => viewportRef.current?.removeEventListener('scroll', onScrollHandler);
    }, [onScrollHandler, setNewMonthsInView]);

    const inViewInterval = useMemo(() => getInViewInterval(monthsInView), [monthsInView]);

    return [
        {
            inViewInterval,
            monthsInView,
            months,
            viewportRef: viewportRefCallback,
            canGoBackwards: monthsInView?.[0] !== 0,
            canGoForwards: monthsInView?.[1] !== months.length - 1,
        },
        viewActions,
    ] as const;
};

export default usePlannerView;

export type UsePlannerView = ReturnType<typeof usePlannerView>;
export type PlannerViewState = UsePlannerView[0];
export type PlannerViewActions = UsePlannerView[1];
